<script lang="ts" setup>
import type { SocialLoginProvider } from '~kui/types/auth';
import type { StringOptions } from '~kui/types/selectOption';

import { Icon } from '~ds/components/ui/icon';
import { Heading } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';
import { motion } from 'motion-v';

import type { AboutYouAnswers, OnboardingState, OnboardingStepConfig } from './types';

import KUIUnifiedAuthStepPasswordSetup from '../Auth/Step/KUIUnifiedAuthStepPasswordSetup.vue';
import KUIUnifiedOnboardingStepAboutYou from './Step/KUIUnifiedOnboardingStepAboutYou.vue';
import KUIUnifiedOnboardingStepEmail from './Step/KUIUnifiedOnboardingStepEmail.vue';
import KUIUnifiedOnboardingStepMarketing from './Step/KUIUnifiedOnboardingStepMarketing.vue';
import KUIUnifiedOnboardingStepOtp from './Step/KUIUnifiedOnboardingStepOtp.vue';
import KUIUnifiedOnboardingStepTerms from './Step/KUIUnifiedOnboardingStepTerms.vue';
import KUIUnifiedOnboardingStepTooYoung from './Step/KUIUnifiedOnboardingStepTooYoung.vue';

/**
 * The consumer resolves `termsPath` (it carries the cluster path prefix) and passes
 * `marketingOptInDefault` from the student's state, which the server decides. `countryStates` feeds the
 * about-you step's select, and `aboutYouLatestBirthMonthYear` (YYYY-MM) is that step's picker
 * cutoff; unset offers every month up to today.
 */
interface Props {
  step: OnboardingStepConfig;
  state: OnboardingState;
  loading?: boolean;
  stepError?: string | null;
  isResent?: boolean;
  resendLoading?: boolean;
  resendNonce?: number;
  termsPath?: string;
  countryStates?: StringOptions[];
  marketingOptInDefault?: boolean;
  aboutYouLatestBirthMonthYear?: string;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  stepError: null,
  isResent: false,
  resendLoading: false,
  resendNonce: 0,
  termsPath: '/terms',
  countryStates: () => [],
  marketingOptInDefault: false,
  aboutYouLatestBirthMonthYear: undefined,
});

const emit = defineEmits<{
  advance: [];
  reset: [];
  emailSubmit: [email: string];
  otpSubmit: [code: string];
  otpResend: [];
  aboutYouSubmit: [payload: AboutYouAnswers];
  tooYoungBackHome: [];
  termsSubmit: [payload: { marketingOptIn: boolean | null }];
  marketingSubmit: [payload: { optIn: boolean }];
  passwordSetupSubmit: [password: string];
  passwordSetupSkip: [];
  socialLogin: [provider: SocialLoginProvider];
}>();

const isMessageOnly = computed(() => !props.step.component);

const stepResult = computed(() =>
  props.stepError ? { success: false, message: props.stepError } : null,
);

// Must match the grid-template-rows transition duration below so the form has
// fully slid out before we advance.
const COLLAPSE_DURATION_MS = 500;

const active = ref(false);
const expanded = ref(false);
const completing = ref(false);
const revealOnArrive = ref(false);

// Set true when the active form step emits its submit, so the loading watch can
// tell a submit-driven loading cycle from any other loading toggle.
const awaitingSubmitResult = ref(false);

const MESSAGE_FADE_MS = 300;
const MESSAGE_REVEAL_SETTLE_MS = COLLAPSE_DURATION_MS + 50;
const MESSAGE_ARRIVE_SETTLE_MS = MESSAGE_FADE_MS;

const displayedMessage = ref(props.step.blinkyMessage);
const messageVisible = ref(true);
let messageTimer: ReturnType<typeof setTimeout> | undefined;
let advanceTimer: ReturnType<typeof setTimeout> | undefined;

const headingWrapperRef = ref<HTMLElement | null>(null);
const headingHeight = ref<string | undefined>(undefined);

const syncHeadingHeight = () => {
  if (headingWrapperRef.value) {
    headingHeight.value = `${headingWrapperRef.value.scrollHeight}px`;
  }
};

/**
 * The last step redirects, so this shell is torn down by a route change rather than by anything it
 * decides. Clearing the timers is not enough on its own: a callback already past its `await` resumes
 * after the teardown and writes to a tree Vue has dropped, which is a patch against a vnode that no
 * longer has an element.
 */
let tornDown = false;

const hideMessage = () => {
  clearTimeout(messageTimer);
  messageVisible.value = false;
};

const showMessage = (delay: number) => {
  clearTimeout(messageTimer);
  messageTimer = setTimeout(async () => {
    displayedMessage.value = props.step.blinkyMessage;
    await nextTick();
    if (tornDown) {
      return;
    }
    syncHeadingHeight();
    messageVisible.value = true;
  }, delay);
};

onMounted(() => {
  nextTick(syncHeadingHeight);
});

onBeforeUnmount(() => {
  tornDown = true;
  clearTimeout(messageTimer);
  clearTimeout(advanceTimer);
});

const reveal = () => {
  active.value = true;
  nextTick(() => {
    if (tornDown) {
      return;
    }
    expanded.value = true;
  });
};

watch(() => props.step.id, () => {
  expanded.value = false;
  completing.value = false;

  const willReveal = !!props.step.component && revealOnArrive.value;
  if (willReveal) {
    reveal();
  }
  else {
    active.value = false;
  }
  revealOnArrive.value = false;

  hideMessage();
  showMessage(willReveal ? MESSAGE_REVEAL_SETTLE_MS : MESSAGE_ARRIVE_SETTLE_MS);
});

onMounted(() => {
  if (props.step.component) {
    reveal();
  }
});

// Slide the form back out via the grid, then advance.
const handleStepComplete = () => {
  completing.value = true;
  expanded.value = false;
  hideMessage();
  clearTimeout(advanceTimer);
  advanceTimer = setTimeout(() => emit('advance'), COLLAPSE_DURATION_MS);
};

// Submit-settle: a successful submit is a loading true→false transition while we
// were awaiting a submit result and no stepError arrived.
watch(() => props.loading, (now, prev) => {
  if (awaitingSubmitResult.value && prev && !now) {
    awaitingSubmitResult.value = false;
    if (!props.stepError) {
      handleStepComplete();
    }
  }
});

const onEmailSubmit = (email: string) => {
  awaitingSubmitResult.value = true;
  emit('emailSubmit', email);
};

const onOtpSubmit = (code: string) => {
  awaitingSubmitResult.value = true;
  emit('otpSubmit', code);
};

const onAboutYouSubmit = (payload: AboutYouAnswers) => {
  awaitingSubmitResult.value = true;
  emit('aboutYouSubmit', payload);
};

const onTermsSubmit = (payload: { marketingOptIn: boolean | null }) => {
  awaitingSubmitResult.value = true;
  emit('termsSubmit', payload);
};

const onMarketingSubmit = (payload: { optIn: boolean }) => {
  awaitingSubmitResult.value = true;
  emit('marketingSubmit', payload);
};

const passwordSetupRef = ref<{ setError: (message: string) => void } | null>(null);

// The password form keeps its own field errors, so a refusal is handed to it rather than shown here.
watch(() => props.stepError, (message) => {
  if (message && props.step.component === 'passwordSetup') {
    passwordSetupRef.value?.setError(message);
  }
});

const onPasswordSetupSubmit = (password: string) => {
  awaitingSubmitResult.value = true;
  emit('passwordSetupSubmit', password);
};

const onPasswordSetupSkip = () => {
  emit('passwordSetupSkip');
  handleStepComplete();
};

const awaitingTap = computed(
  () => !props.loading && !active.value && !completing.value,
);

const handleShellTap = () => {
  if (!awaitingTap.value) {
    return;
  }
  if (isMessageOnly.value) {
    revealOnArrive.value = true;
    hideMessage();
    emit('advance');
    return;
  }
  reveal();
};

const handleShellKeydown = (event: KeyboardEvent) => {
  if (!awaitingTap.value) {
    return;
  }
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    handleShellTap();
  }
};

const handleResetRequest = () => {
  revealOnArrive.value = true;
  hideMessage();
  emit('reset');
};
</script>

<template>
  <div
    data-testid="onboarding-shell"
    class="relative flex h-dvh flex-col items-center justify-center bg-background px-6"
    :class="{ 'cursor-pointer': awaitingTap }"
    :role="awaitingTap ? 'button' : undefined"
    :tabindex="awaitingTap ? 0 : undefined"
    @click="handleShellTap"
    @keydown="handleShellKeydown"
  >
    <motion.div
      class="mb-8"
      :initial="{ scale: 0 }"
      :animate="{ scale: 1 }"
      :transition="{ type: 'spring', stiffness: 200, damping: 20, duration: 0.1 }"
    >
      <KUIUnifiedBlinky size="xl" />
    </motion.div>

    <div class="relative mx-auto w-full max-w-sm">
      <div
        ref="headingWrapperRef"
        class="overflow-hidden transition-[height] duration-500 ease-out"
        :style="{ height: headingHeight }"
      >
        <Heading
          size="md"
          as="h1"
          class="text-center transition-opacity duration-300"
          :class="messageVisible ? 'opacity-100' : 'opacity-0'"
        >
          {{ displayedMessage }}
        </Heading>
      </div>

      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="awaitingTap"
          class="pointer-events-none absolute inset-x-0 top-full mt-3 flex justify-end pe-4"
          data-testid="onboarding-shell-tap-hint"
        >
          <div class="animate-tap-pulse rounded-full bg-white p-2">
            <Icon
              size="sm"
              :name="ICONS.tapScreen"
              class="opacity-50"
            />
          </div>
        </div>
      </Transition>
    </div>

    <div
      v-if="step.component"
      class="grid w-full transition-[grid-template-rows] duration-500 ease-out"
      :style="{ gridTemplateRows: expanded ? '1fr' : '0fr' }"
    >
      <div class="overflow-hidden">
        <div
          v-if="active"
          :inert="completing"
          data-testid="onboarding-shell-form-area"
          class="mx-auto mt-8 w-full max-w-sm pb-2 transition-opacity duration-300"
          :class="expanded ? 'opacity-100 delay-200' : 'opacity-0'"
          @click.stop
        >
          <KUIUnifiedOnboardingStepEmail
            v-if="step.component === 'email'"
            :initial-email="state.email"
            :result="stepResult"
            :loading="loading"
            @submit="onEmailSubmit"
            @social-login="emit('socialLogin', $event)"
          />
          <KUIUnifiedOnboardingStepOtp
            v-else-if="step.component === 'otp'"
            :email="state.email"
            :initial-code="state.otpCode"
            :result="stepResult"
            :loading="loading"
            :is-resent="isResent"
            :resend-loading="resendLoading"
            :resend-nonce="resendNonce"
            @submit="onOtpSubmit"
            @resend="emit('otpResend')"
            @change-email="handleResetRequest"
          />
          <KUIUnifiedOnboardingStepAboutYou
            v-else-if="step.component === 'aboutYou'"
            :country-states="countryStates"
            :latest-birth-month-year="aboutYouLatestBirthMonthYear"
            :fields="state.aboutYouFields"
            :initial-first-name="state.firstName"
            :initial-last-name="state.lastName"
            :result="stepResult"
            :loading="loading"
            @submit="onAboutYouSubmit"
          />
          <KUIUnifiedOnboardingStepTooYoung
            v-else-if="step.component === 'tooYoung'"
            @back-home="emit('tooYoungBackHome')"
          />
          <KUIUnifiedOnboardingStepMarketing
            v-else-if="step.component === 'marketing'"
            :result="stepResult"
            :loading="loading"
            :marketing-opt-in-default="marketingOptInDefault"
            @submit="onMarketingSubmit"
          />
          <KUIUnifiedOnboardingStepTerms
            v-else-if="step.component === 'terms'"
            :result="stepResult"
            :loading="loading"
            :terms-path="termsPath"
            :show-marketing-opt-in="state.canBeMarketed"
            :marketing-opt-in-default="marketingOptInDefault"
            :age-claim-required="!state.birthMonthYear"
            :email="state.email"
            @submit="onTermsSubmit"
            @change-email="handleResetRequest"
          />
          <KUIUnifiedAuthStepPasswordSetup
            v-else-if="step.component === 'passwordSetup'"
            ref="passwordSetupRef"
            :email="state.email"
            :loading="loading"
            hide-intro
            @submit="onPasswordSetupSubmit"
            @skip="onPasswordSetupSkip"
          />
        </div>
      </div>
    </div>
  </div>
</template>
